// Copyright (c) 2025 Digital Asset (Switzerland) GmbH and/or its affiliates. All rights reserved.
// SPDX-License-Identifier: Apache-2.0
import { useState } from 'react';
import { Choice, Template } from '@daml/types';
import { SigningMode, useModuleSigningMode } from '../../../contexts/ModuleContext';
import { LedgerCreateArgs, LedgerExerciseArgs } from '../../../utils/LedgerApiClientV2';
import useMutate from '../useMutate';
import { useLedgerCreate } from './useLedgerCreate';
import { useLedgerExercise } from './useLedgerExercise';

type SigningStatus = 'initial' | 'pending' | 'sig_requested' | 'success' | 'error';

type CreateSubmission<T extends object> = {
  submit: (args: LedgerCreateArgs<T>) => Promise<void>;
  status: SigningStatus;
};

export const useCreateSubmission = <T extends object, K, I extends string>(
  template: Template<T, K, I>,
  successMsg: string,
  errorMsg: string,
): CreateSubmission<T> => {
  const [status, setStatus] = useState<SigningStatus>('initial');

  const mode = useModuleSigningMode();

  const createM = useLedgerCreate<T, K, I>(template);

  const mutate = useMutate();

  const ledgerSubmit = async (args: LedgerCreateArgs<T>) => {
    setStatus('pending');
    return mutate(createM, args, successMsg, errorMsg)
      .then(() => setStatus('success'))
      .catch(() => setStatus('error'));
  };

  const submit = async (args: LedgerCreateArgs<T>) => {
    switch (mode) {
      case SigningMode.DFNS:
        break;
      default:
        await ledgerSubmit(args);
    }
  };

  return { status, submit };
};

type ExerciseSubmission<T extends object, C> = {
  submit: (args: LedgerExerciseArgs<T, C>) => Promise<void>;
  status: SigningStatus;
};

export const useExerciseSubmission = <T extends object, C, R, K>(
  choice: Choice<T, C, R, K>,
  successMsg: string,
  errorMsg: string,
): ExerciseSubmission<T, C> => {
  const [status, setStatus] = useState<SigningStatus>('initial');
  const mode = useModuleSigningMode();
  const exerciseM = useLedgerExercise<T, C, R, K>(choice);

  const mutate = useMutate();

  const ledgerSubmit = async (args: LedgerExerciseArgs<T, C>) => {
    setStatus('pending');
    return mutate(exerciseM, args, successMsg, errorMsg)
      .then(() => setStatus('success'))
      .catch((err) => {
        setStatus('error');
        throw err;
      });
  };

  const submit = async (args: LedgerExerciseArgs<T, C>) => {
    switch (mode) {
      case SigningMode.DFNS:
        break;
      default:
        await ledgerSubmit(args);
    }
  };
  return { status, submit };
};
